import { useEffect, useMemo, useRef, useState } from 'react';

import { WIDGETS_DOMAIN } from '@/constants/env';
import { useAppSelector } from '@/redux/hooks';

const SCRIPT_ID = 'ambassador-widget-script';
const INSTANCE_KEY = 'ambassadorWidgets';
// const WIDGET_SCRIPT = 'src/index.ts'; // If you are using this against development project
const WIDGET_SCRIPT = 'widgets.js';

type LoadResult = {
  script: HTMLScriptElement | null;
  isLoading: boolean;
  loaded: boolean;
  error: Error | null;
};

type Options = {
  nonce?: string;
};

function findExistingScript() {
  return document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
}

/**
 * Insert the widget script.
 * - Configures window[key].q before loading the script.
 * - Reuses the same <script> if it already exists.
 * - Applies nonce and security attributes.
 */
export function useWidgetScript({ nonce }: Options): LoadResult {
  const hasPortal3Access = useAppSelector((state) => state.auth.companyAccount?.portal_v3_access);
  const uid = useAppSelector((state) => state.auth.companyAccount?.universal_id);

  const scriptRef = useRef<HTMLScriptElement | null>(findExistingScript());
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [error, setError] = useState<Error | null>(null);

  // URL final with ?key=
  const finalSrc = useMemo(() => {
    const u = new URL(WIDGET_SCRIPT, WIDGETS_DOMAIN || window.location.origin || undefined);
    u.searchParams.set('key', INSTANCE_KEY);
    return u.toString();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !WIDGETS_DOMAIN) return; // SSR guard
    if (scriptRef.current) return; // script already exists
    if (!uid || !hasPortal3Access) return; // uid and hasPortal3Access are required

    setStatus('loading');
    // Prepare window object for configuration
    const w = window as any;
    w[INSTANCE_KEY] = w[INSTANCE_KEY] || {};
    w[INSTANCE_KEY].q = { uid, isPortal: true };

    // deduplicate / reuse script if it already exists
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.type = 'module';
    script.src = finalSrc;
    script.nonce = nonce || undefined;
    script.crossOrigin = 'anonymous';
    const onLoad = () => {
      setStatus('loaded');
    };
    const onError = () => {
      setError(new Error(`Failed to load widget script: ${finalSrc}`));
      setStatus('error');
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    scriptRef.current = script;
    document.head.appendChild(script);
  }, [finalSrc, uid, hasPortal3Access, nonce]);

  return { script: scriptRef.current, isLoading: status === 'loading', loaded: status === 'loaded', error };
}
